import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

interface KeyPressProps {
    keys: string[];
    specialKey?: string;
    callback: (event: any) => void;
    node?: Node | null;
}

/**
 * React hook to bind keyboard touch with action
 * It's higly recommeded to provide a node since the document can have only one key and one action working at the same time
 * With a node, you need to add the tabIndex attribute to the node to make it focusable, and you might want to auto focus it on component mount
 * @example ```ts
 *      useKeyPress({keys: ["Escape"], callback: () => console.log("ctrl + k pressed"), node: elementRef.current});
 * ```
 * ```tsx
 *  useEffect(() => {
 *      if (elementRef.current)
 *          elementRef.current.focus()
 *  }, [elementRef.current])
 * // ...
 *  <div tabIndex={-1} ref={elementRef}>
 * ```
 * @param keys Array of keys to listen to
 * @param specialKey Special key to listen to (ctrlKey, shiftKey, etc...)
 * ```ts
 *      useKeyPress({keys: ["k"], specialKey: "ctrlKey", callback: () => console.log("ctrl + k pressed")});
 * ```
 * @param callback Callback function to call when the key is pressed
 * @param node Node to listen to (default: document)
 */
export const useKeyPress = ({ keys, specialKey = "", callback, node = null }: KeyPressProps) => {
    // implement the callback ref pattern
    const callbackRef = useRef(callback);
    useLayoutEffect(() => {
        callbackRef.current = callback;
    });

    // handle what happens on key press
    const handleKeyPress = useCallback(
        (event: any) => {
            // event.preventDefault()
            // check if one of the key is part of the ones we want
            if (
                keys.some((key: any) => event.key === key) &&
                ((specialKey && event[specialKey] === true) || specialKey == "")
            ) {
                event.preventDefault();
                callbackRef.current(event);
            }
        },
        [keys],
    );

    useEffect(() => {
        // target is either the provided node or the document
        const targetNode = node ?? document;
        // attach the event listener
        targetNode && targetNode.addEventListener("keydown", handleKeyPress);

        // remove the event listener
        return () => targetNode && targetNode.removeEventListener("keydown", handleKeyPress);
    }, [handleKeyPress, node]);
};
